import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DatePickerHR } from "../components/DatePickerHR";
import { useSort, SortHead } from "../components/SortHead";
import api from "../lib/api";

const ACTION_COLORS = {
  "unos": "bg-[#0D9488]/10 text-[#0D9488]",
  "izmjena": "bg-[#D97706]/10 text-[#D97706]",
  "brisanje": "bg-[#DC2626]/10 text-[#DC2626]",
  "uvoz izvoda": "bg-[#0D9488]/10 text-[#0D9488]",
  "knjiženje": "bg-[#D97706]/10 text-[#D97706]",
  "ostvarenje": "bg-[#0D9488]/10 text-[#0D9488]",
  "prijava": "bg-[#2E7D5B]/10 text-[#2E7D5B]",
};

const fmtTs = (iso) => {
  const d = new Date(iso);
  return d.toLocaleString("hr-HR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};
const fmtTime = (iso) => new Date(iso).toLocaleTimeString("hr-HR", { hour: "2-digit", minute: "2-digit" });

const AUTO_USERS = ["Rent a Car most", "HR most"];

export default function ActivityPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(200);
  const [users, setUsers] = useState([]);
  const [userFilter, setUserFilter] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");
  const [deletions, setDeletions] = useState(0);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [tab, setTab] = useState("user");

  useEffect(() => {
    api.get("/activity/users").then((r) => setUsers(r.data));
  }, []);

  useEffect(() => {
    const params = { limit, source: tab };
    if (userFilter !== "all") params.user_filter = userFilter;
    if (actionFilter !== "all") params.action_filter = actionFilter;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get("/activity", { params }).then((r) => { setItems(r.data.items); setTotal(r.data.total); setDeletions(r.data.deletions || 0); });
  }, [userFilter, actionFilter, dateFrom, dateTo, limit, tab]);

  const hasFilters = userFilter !== "all" || actionFilter !== "all" || dateFrom || dateTo;

  const aAccessors = {
    ts: (a) => a.timestamp,
    user: (a) => a.user.toLowerCase(),
    action: (a) => a.action,
  };
  const { sorted: sortedItems, sort, toggle } = useSort(items, aAccessors);

  const displayRows = [];
  for (const a of sortedItems) {
    const prev = displayRows[displayRows.length - 1];
    if (tab === "auto" && prev && prev.user === a.user && prev.action === a.action && prev.description === a.description) {
      prev.count += 1;
      prev.tsOld = a.timestamp;
    } else {
      displayRows.push({ ...a, count: 1, tsNew: a.timestamp, tsOld: a.timestamp });
    }
  }
  const tabUsers = users.filter((u) => (tab === "auto" ? AUTO_USERS.includes(u) : !AUTO_USERS.includes(u)));

  return (
    <div className="space-y-6 fade-up" data-testid="activity-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-1">Tko je što unio</p>
        <h1 className="font-heading text-4xl font-bold tracking-tight text-[#0F172A]">Aktivnost</h1>
      </div>

      <div className="flex gap-0 border-b border-[#E2E8F0]">
        <button
          data-testid="activity-tab-user"
          onClick={() => { setTab("user"); setUserFilter("all"); setLimit(200); }}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors duration-200 ${tab === "user" ? "border-[#0D9488] text-[#0D9488]" : "border-transparent text-[#64748B] hover:text-[#0F172A]"}`}
        >
          Korisnici
        </button>
        <button
          data-testid="activity-tab-auto"
          onClick={() => { setTab("auto"); setUserFilter("all"); setLimit(200); }}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors duration-200 ${tab === "auto" ? "border-[#0D9488] text-[#0D9488]" : "border-transparent text-[#64748B] hover:text-[#0F172A]"}`}
          title="Automatski zapisi Rent a Car i HR mostova (sinkronizacije, planovi)"
        >
          Automatski (mostovi)
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-3 bg-white border border-[#E2E8F0] rounded-sm p-4">
        <div className="space-y-1">
          <p className="text-xs text-[#475569]">Korisnik</p>
          <Select value={userFilter} onValueChange={setUserFilter}>
            <SelectTrigger data-testid="activity-user-filter" className="w-48 rounded-sm bg-white">
              <SelectValue placeholder="Svi korisnici" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Svi korisnici</SelectItem>
              {tabUsers.map((u) => (
                <SelectItem key={u} value={u}>{u}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <p className="text-xs text-[#475569]">Radnja</p>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger data-testid="activity-action-filter" className="w-40 rounded-sm bg-white">
              <SelectValue placeholder="Sve radnje" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Sve radnje</SelectItem>
              <SelectItem value="brisanje">brisanje</SelectItem>
              <SelectItem value="unos">unos</SelectItem>
              <SelectItem value="izmjena">izmjena</SelectItem>
              <SelectItem value="uvoz izvoda">uvoz izvoda</SelectItem>
              <SelectItem value="knjiženje">knjiženje</SelectItem>
              <SelectItem value="ostvarenje">ostvarenje</SelectItem>
              <SelectItem value="usklađivanje">usklađivanje</SelectItem>
              <SelectItem value="prijava">prijava</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <p className="text-xs text-[#475569]">Od datuma</p>
          <DatePickerHR data-testid="activity-date-from" value={dateFrom} onChange={setDateFrom} className="w-44" />
        </div>
        <div className="space-y-1">
          <p className="text-xs text-[#475569]">Do datuma</p>
          <DatePickerHR data-testid="activity-date-to" value={dateTo} onChange={setDateTo} className="w-44" />
        </div>
        {hasFilters && (
          <Button
            data-testid="activity-clear-filters"
            variant="outline"
            className="rounded-sm border-[#CBD5E1] text-[#475569]"
            onClick={() => { setUserFilter("all"); setActionFilter("all"); setDateFrom(""); setDateTo(""); }}
          >
            Očisti filtre
          </Button>
        )}
        <div className="ml-auto flex items-center gap-3 self-center">
          {deletions > 0 && (
            <button
              data-testid="activity-deletions-count"
              onClick={() => setActionFilter(actionFilter === "brisanje" ? "all" : "brisanje")}
              className={`text-xs font-semibold px-2 py-1 rounded-sm border transition-colors duration-200 ${actionFilter === "brisanje" ? "bg-[#DC2626] text-white border-[#DC2626]" : "bg-[#DC2626]/10 text-[#DC2626] border-[#DC2626]/30 hover:bg-[#DC2626]/20"}`}
              title="Prikaži samo brisanja"
            >
              🗑 {deletions} brisanja
            </button>
          )}
          <p className="text-xs text-[#64748B]" data-testid="activity-count">prikazano {items.length} od {total} zapisa</p>
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F1F5F9] hover:bg-[#F1F5F9]">
              <SortHead label="Vrijeme" sortKey="ts" sort={sort} onToggle={toggle} className="w-40" />
              <SortHead label="Korisnik" sortKey="user" sort={sort} onToggle={toggle} className="w-40" />
              <SortHead label="Radnja" sortKey="action" sort={sort} onToggle={toggle} className="w-32" />
              <TableHead>Opis</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="activity-table-body">
            {sortedItems.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center text-[#64748B] py-10">{hasFilters ? "Nema aktivnosti za odabrane filtre." : tab === "auto" ? "Nema automatskih zapisa mostova." : "Još nema zabilježenih aktivnosti."}</TableCell></TableRow>
            )}
            {displayRows.map((a) => (
              <TableRow key={a.id} className={a.action === "brisanje" ? "bg-[#DC2626]/[0.07] hover:bg-[#DC2626]/[0.12] border-l-2 border-l-[#DC2626] transition-colors duration-200" : "hover:bg-[#F8FAFC] transition-colors duration-200"}>
                <TableCell className="tabular-nums text-sm text-[#475569] whitespace-nowrap">
                  {a.count > 1 ? `${fmtTs(a.tsOld)} – ${fmtTime(a.tsNew)}` : fmtTs(a.timestamp)}
                </TableCell>
                <TableCell className="text-sm font-semibold">{a.user}</TableCell>
                <TableCell>
                  {a.action === "brisanje" ? (
                    <Badge className="rounded-sm text-xs font-bold bg-[#DC2626] text-white hover:bg-[#DC2626] gap-1" data-testid={`deletion-badge-${a.id}`}>
                      <Trash2 className="w-3 h-3" /> OBRISANO
                    </Badge>
                  ) : (
                    <Badge className={`rounded-sm text-xs font-medium hover:bg-inherit ${ACTION_COLORS[a.action] || "bg-[#F1F5F9] text-[#475569]"}`}>{a.action}</Badge>
                  )}
                </TableCell>
                <TableCell className={`text-sm ${a.action === "brisanje" ? "text-[#DC2626] font-medium" : ""}`}>
                  {a.description}
                  {a.count > 1 && (
                    <Badge data-testid={`activity-group-count-${a.id}`} className="ml-2 rounded-sm text-[10px] font-bold bg-[#0D9488]/10 text-[#0D9488] hover:bg-[#0D9488]/10">×{a.count}</Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {items.length < total && (
        <div className="flex justify-center">
          <Button data-testid="activity-load-more" variant="outline" className="rounded-sm border-[#CBD5E1]" onClick={() => setLimit((l) => l + 500)}>
            Učitaj još ({total - items.length} preostalo)
          </Button>
        </div>
      )}
    </div>
  );
}
